<template>
  <LayoutMain>
    <template #header>
      <PageHeader :icon="ListOrdered" :title="t('sevenFacts.title')" />
    </template>

    <!-- Loading State -->
    <div v-if="gameState.phase === 'LOADING'" class="flex justify-center p-8">
      <Loader2 class="h-8 w-8 animate-spin text-rogue-vibrant-green" />
    </div>

    <!-- Warm Entry Phase -->
    <div v-else-if="gameState.phase === 'FACT_ENTRY'" class="space-y-6">
      <SevenFactsWarmEntry
        :is-first-time="!uiState.isEditMode && gameState.userFacts.length === 0"
        :existing-facts="factInputs"
        :is-submitting="uiState.isSubmittingFacts"
        :on-submit="saveFactsAndContinue"
      />

      <!-- Edit Mode Actions (only shown in edit mode) -->
      <div v-if="uiState.isEditMode" class="max-w-2xl mx-auto">
        <Card class="p-4">
          <div class="flex gap-3">
            <Button variant="outline" class="flex-1" @click="cancelEdit">
              {{ t('sevenFacts.cancel') }}
            </Button>
            <Button
              variant="outline"
              class="flex-1 text-red-600 border-red-300 hover:bg-red-50"
              @click="uiState.showDeleteConfirmation = true"
            >
              <Trash2 class="h-4 w-4 mr-2" />
              {{ t('sevenFacts.deleteAllFacts') }}
            </Button>
          </div>
        </Card>
      </div>
    </div>

    <!-- Game Phase -->
    <div v-else-if="gameState.phase === 'PLAYING'" class="max-w-2xl mx-auto space-y-6">
      <!-- Game Header with Streak -->
      <div class="flex justify-between items-center">
        <h2 class="text-2xl font-bold text-rogue-deep-forest">&nbsp;</h2>
        <div class="flex items-center gap-3">
          <div
            v-if="gameState.streak > 0"
            class="text-sm font-medium text-green-600 bg-green-50 px-3 py-1 rounded-full"
          >
            {{ t('sevenFacts.streak') }}: {{ gameState.streak }} 🔥
          </div>
          <Button
            variant="outline"
            size="sm"
            class="text-gray-600 hover:text-rogue-deep-forest border-gray-300 text-sm"
            :aria-label="t('sevenFacts.editFacts')"
            @click="editFacts"
          >
            <Settings class="h-6 w-6" />
          </Button>
        </div>
      </div>

      <!-- Photo Grid (2x2) -->
      <div
        v-if="!uiState.isLoadingRound"
        class="bg-white rounded-xl border border-neutral p-6 shadow-sm"
      >
        <p class="text-center text-sm font-medium text-gray-600 mb-4">
          {{ t('sevenFacts.playInstruction') }}
        </p>

        <div class="grid grid-cols-2 gap-4 max-w-md mx-auto mb-6">
          <button
            v-for="(user, index) in gameState.currentRound.options"
            :key="user.id"
            :disabled="uiState.showingResult"
            class="relative aspect-square rounded-lg overflow-hidden transition-all duration-300"
            :class="getCardClasses(user.id, index)"
            :aria-label="`Select ${user.name}`"
            @click="handleGuess(user.id, index)"
          >
            <!-- eslint-disable vue/html-self-closing -->
            <img
              :src="getUserPhoto(user)"
              :alt="user.name"
              class="w-full h-full object-cover bg-gray-100"
              @error="handleImageError"
            />
            <!-- eslint-enable vue/html-self-closing -->

            <!-- Name overlay -->
            <div
              class="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-sm p-2 text-center"
            >
              {{ user.name }}
            </div>

            <!-- Result overlay -->
            <transition name="fade">
              <div
                v-if="uiState.showingResult && user.id === gameState.currentRound.correctUserId"
                class="absolute inset-0 bg-green-500/20 flex items-center justify-center"
              >
                <CheckCircle class="h-12 w-12 text-green-600" />
              </div>
            </transition>
          </button>
        </div>

        <!-- Fact Display -->
        <div class="bg-gray-100 rounded-lg p-5 text-center">
          <p class="text-sm font-medium text-gray-500 mb-2">
            {{ t('sevenFacts.factPrompt') }}
          </p>
          <p class="text-xl font-semibold text-rogue-deep-forest">
            "{{ gameState.currentRound.fact?.text }}"
          </p>
        </div>

        <!-- Result Message -->
        <transition name="slide-up">
          <div
            v-if="uiState.showingResult && !gameState.currentRound.isCorrect"
            class="bg-red-50 border border-red-200 rounded-lg p-4 text-center mt-4"
            aria-live="polite"
          >
            <p class="text-red-700">
              <span>{{ t('sevenFacts.itWas') }}</span>
              <strong v-text="getCorrectUserName()" />!
            </p>
          </div>
        </transition>

        <transition name="slide-up">
          <div
            v-if="uiState.showingResult && gameState.currentRound.isCorrect"
            class="bg-green-50 border border-green-200 rounded-lg p-4 text-center mt-4"
            aria-live="polite"
          >
            <p class="text-green-700">
              <strong>{{ t('sevenFacts.correct') }}</strong> {{ t('sevenFacts.thatWas') }}
              {{ getCorrectUserName() }}.
            </p>
          </div>
        </transition>
      </div>

      <!-- Loading next round -->
      <div v-else class="bg-white rounded-xl border border-neutral p-6 shadow-sm text-center">
        <Loader2 class="h-8 w-8 animate-spin text-rogue-vibrant-green mx-auto mb-4" />
        <p class="text-gray-600">
          {{ t('sevenFacts.loadingNextRound') }}
        </p>
      </div>
    </div>

    <!-- Waiting Phase -->
    <div v-else-if="gameState.phase === 'WAITING_FOR_PLAYERS'" class="max-w-2xl mx-auto">
      <div class="bg-white rounded-xl border border-neutral p-8 shadow-sm text-center">
        <Users class="h-16 w-16 text-gray-400 mx-auto mb-4" />
        <h3 class="text-xl font-semibold text-rogue-deep-forest mb-2">
          {{ t('sevenFacts.waitingForMorePlayers') }}
        </h3>
        <p class="text-gray-600 mb-4">
          {{ t('sevenFacts.needMorePlayers') }}
        </p>
        <div class="mb-6 rounded-lg border border-neutral bg-gray-50 p-4 text-left">
          <div class="flex items-center justify-between gap-4">
            <span class="text-sm font-medium text-gray-600">{{
              t('sevenFacts.yourFactCount')
            }}</span>
            <span class="text-sm font-semibold text-rogue-deep-forest">
              {{ gameState.userFacts.length }}/7
            </span>
          </div>
          <div class="mt-3 h-2 rounded-full bg-gray-200 overflow-hidden">
            <div
              class="h-full bg-primary transition-all"
              :style="{ width: `${profileStrengthPercent}%` }"
            />
          </div>
          <p class="mt-3 text-sm text-gray-600">
            {{ profileStrengthMessage }}
          </p>
        </div>
        <p class="text-gray-600 mb-6">
          {{ t('sevenFacts.waitingImproveHint') }}
        </p>
        <div class="flex flex-wrap gap-3 justify-center">
          <Button variant="primary" @click="editFacts">
            <Settings class="h-4 w-4 mr-2" />
            {{ t('sevenFacts.improveMyFacts') }}
          </Button>
          <Button variant="outline" @click="checkPlayerCount">
            {{ t('sevenFacts.checkAgain') }}
          </Button>
          <Button variant="ghost" @click="$router.back()">
            {{ t('sevenFacts.goBack') }}
          </Button>
        </div>
      </div>
    </div>

    <!-- Error Display -->
    <div
      v-if="errorMessage"
      class="fixed bottom-4 right-4 bg-red-500 text-white p-4 rounded-lg shadow-lg z-50 max-w-sm"
    >
      <div class="flex items-center gap-2">
        <AlertCircle class="h-5 w-5" />
        <span>{{ errorMessage }}</span>
        <Button
          variant="ghost"
          size="icon"
          class="text-white hover:bg-white/20 ml-2"
          @click="errorMessage = ''"
        >
          <X class="h-4 w-4" />
        </Button>
      </div>
    </div>

    <!-- Delete Facts Confirmation Modal -->
    <div
      v-if="uiState.showDeleteConfirmation"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    >
      <div class="bg-white rounded-lg p-6 max-w-sm w-full">
        <div class="text-center">
          <AlertTriangle class="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 class="text-lg font-semibold text-gray-900 mb-2">
            {{ t('sevenFacts.deleteAllFactsConfirm') }}
          </h3>
          <p class="text-gray-600 mb-6">
            {{ t('sevenFacts.deleteAllFactsDescription') }}
          </p>
          <div class="flex gap-3">
            <Button
              variant="outline"
              class="flex-1"
              @click="uiState.showDeleteConfirmation = false"
            >
              {{ t('sevenFacts.cancel') }}
            </Button>
            <Button
              variant="danger"
              class="flex-1"
              :disabled="uiState.isDeletingFacts"
              @click="deleteAllFacts"
            >
              <Loader2 v-if="uiState.isDeletingFacts" class="h-4 w-4 mr-2 animate-spin" />
              {{ uiState.isDeletingFacts ? t('sevenFacts.deleting') : t('sevenFacts.deleteAll') }}
            </Button>
          </div>
        </div>
      </div>
    </div>
  </LayoutMain>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { supabase } from '../lib/supabase'
import { getGameImage } from '../lib/imageUtils'
import {
  ListOrdered,
  Loader2,
  Users,
  CheckCircle,
  AlertCircle,
  X,
  AlertTriangle,
  Trash2,
  Settings
} from 'lucide-vue-next'
import LayoutMain from '../components/ui/LayoutMain.vue'
import PageHeader from '../components/ui/PageHeader.vue'
import Button from '../components/ui/Button.vue'
import Card from '../components/ui/Card.vue'
import SevenFactsWarmEntry from '../components/SevenFactsWarmEntry.vue'
import { useI18n } from 'vue-i18n'

const router = useRouter()
const authStore = useAuthStore()
const { t } = useI18n()

// Core reactive state structure
const gameState = reactive({
  phase: 'LOADING', // 'LOADING' | 'FACT_ENTRY' | 'PLAYING' | 'WAITING_FOR_PLAYERS'
  userFacts: [],
  currentRound: {
    fact: null,
    options: [],
    correctUserId: null,
    guessedUserId: null,
    isCorrect: null,
    roundId: null
  },
  streak: 0,
  recentFactIds: [],
  totalRounds: 0
})

// UI state
const uiState = reactive({
  isSubmittingFacts: false,
  isLoadingRound: false,
  showingResult: false,
  selectedCardIndex: null,
  isEditMode: false,
  showDeleteConfirmation: false,
  isDeletingFacts: false
})

// Fact inputs for entry phase
const factInputs = ref(['', '', '', '', '', '', ''])
const errorMessage = ref('')

const profileStrengthPercent = computed(() => Math.min(100, (gameState.userFacts.length / 7) * 100))
const profileStrengthMessage = computed(() => {
  if (gameState.userFacts.length >= 7) return t('sevenFacts.profileStrengthComplete')
  if (gameState.userFacts.length >= 5) return t('sevenFacts.profileStrengthStrong')
  return t('sevenFacts.profileStrengthReady')
})

watch(
  () => gameState.streak,
  newStreak => {
    sessionStorage.setItem('sevenFactsStreak', newStreak.toString())
  }
)

onMounted(async () => {
  const savedStreak = sessionStorage.getItem('sevenFactsStreak')
  if (savedStreak) {
    gameState.streak = parseInt(savedStreak)
  }
  await initializeGame()
})

// Core functions
async function initializeGame() {
  try {
    gameState.phase = 'LOADING'

    // Check if user has submitted facts
    const userFacts = await fetchUserFacts()

    if (userFacts.length < 3) {
      gameState.userFacts = userFacts
      factInputs.value = [...userFacts.map(f => f.text), ...Array(7 - userFacts.length).fill('')]
      gameState.phase = 'FACT_ENTRY'
    } else {
      gameState.userFacts = userFacts
      // Check if enough players exist
      const eligiblePlayersCount = await countEligiblePlayers()

      if (eligiblePlayersCount < 4) {
        gameState.phase = 'WAITING_FOR_PLAYERS'
      } else {
        gameState.phase = 'PLAYING'
        await startNewRound()
      }
    }
  } catch (error) {
    console.error('Error initializing game:', error)
    errorMessage.value = t('sevenFacts.loadFailed')
    gameState.phase = 'FACT_ENTRY'
  }
}

async function fetchUserFacts() {
  const { data, error } = await supabase
    .from('facts')
    .select('*')
    .eq('user_id', authStore.user.id)
    .order('created_at', { ascending: true })

  if (error) throw error
  return data || []
}

async function countEligiblePlayers() {
  // Count users who have at least 3 facts (excluding current user)
  const { data, error } = await supabase
    .from('facts')
    .select('user_id')
    .neq('user_id', authStore.user.id)

  if (error) throw error

  // Group by user_id and count facts per user
  const userFactCounts = {}
  data.forEach(fact => {
    userFactCounts[fact.user_id] = (userFactCounts[fact.user_id] || 0) + 1
  })

  // Count users with 3+ facts
  return Object.values(userFactCounts).filter(count => count >= 3).length
}

function normalizeAndValidateFacts(facts) {
  const validFacts = facts
    .map(fact => String(fact || '').trim())
    .filter(Boolean)
    .slice(0, 7)

  if (validFacts.length < 3) {
    throw new Error(t('sevenFacts.enterAtLeastThree'))
  }

  const tooShort = validFacts.some(fact => fact.length < 10)
  if (tooShort) {
    throw new Error(t('sevenFacts.factLengthHint'))
  }

  const uniqueFacts = new Set(validFacts.map(fact => fact.toLocaleLowerCase()))
  if (uniqueFacts.size !== validFacts.length) {
    throw new Error(t('sevenFacts.ensureUnique'))
  }

  return validFacts.map(fact => fact.substring(0, 160))
}

async function saveFactsAndContinue(facts) {
  uiState.isSubmittingFacts = true

  try {
    const validFacts = normalizeAndValidateFacts(facts)

    const { error: deleteError } = await supabase
      .from('facts')
      .delete()
      .eq('user_id', authStore.user.id)

    if (deleteError) throw deleteError

    const { error } = await supabase.from('facts').insert(
      validFacts.map(text => ({
        user_id: authStore.user.id,
        text
      }))
    )

    if (error) throw error

    gameState.userFacts = validFacts
    factInputs.value = [...validFacts, ...Array(7 - validFacts.length).fill('')]
    uiState.isEditMode = false

    await transitionToAvailablePlayState()
  } catch (error) {
    console.error('Error submitting facts:', error)
    throw error
  } finally {
    uiState.isSubmittingFacts = false
  }
}

async function transitionToAvailablePlayState() {
  const eligibleCount = await countEligiblePlayers()

  if (eligibleCount >= 4) {
    gameState.phase = 'PLAYING'
    await startNewRound()
  } else {
    gameState.phase = 'WAITING_FOR_PLAYERS'
  }
}

async function startNewRound() {
  uiState.isLoadingRound = true
  uiState.showingResult = false
  uiState.selectedCardIndex = null

  try {
    // Get users with facts (excluding current user)
    const { data: userFacts, error: factsError } = await supabase
      .from('facts')
      .select(
        `
        id,
        text,
        user_id,
        profiles!inner(
          id,
          name,
          thumbnail_url
        )
      `
      )
      .neq('user_id', authStore.user.id)

    if (factsError) throw factsError

    // Group facts by user
    const usersByFacts = {}
    userFacts.forEach(fact => {
      const userId = fact.user_id
      if (!usersByFacts[userId]) {
        usersByFacts[userId] = {
          user: fact.profiles,
          facts: []
        }
      }
      usersByFacts[userId].facts.push(fact)
    })

    // Filter users with at least 3 facts
    const eligibleUsers = Object.values(usersByFacts).filter(item => item.facts.length >= 3)

    if (eligibleUsers.length < 4) {
      gameState.phase = 'WAITING_FOR_PLAYERS'
      return
    }

    // Select 4 random users
    const selectedUsers = shuffleArray(eligibleUsers).slice(0, 4)

    // Pick one user's fact (avoiding recent facts)
    const factOwner = selectedUsers[Math.floor(Math.random() * 4)]
    const availableFacts = factOwner.facts.filter(f => !gameState.recentFactIds.includes(f.id))

    const selectedFact =
      availableFacts.length > 0
        ? availableFacts[Math.floor(Math.random() * availableFacts.length)]
        : factOwner.facts[0] // Fallback if all facts were recent

    // Update recent facts list (keep last 20)
    gameState.recentFactIds = [selectedFact.id, ...gameState.recentFactIds.slice(0, 19)]

    // Fetch profile photos for selected users
    const userIds = selectedUsers.map(item => item.user.id)
    const { data: photos } = await supabase
      .from('profile_photos')
      .select('*')
      .in('user_id', userIds)
      .order('order', { ascending: true })

    // Attach photos to users
    const usersWithPhotos = selectedUsers.map(item => ({
      ...item.user,
      photos: photos?.filter(p => p.user_id === item.user.id) || []
    }))

    // Set up round
    gameState.currentRound = {
      fact: selectedFact,
      options: shuffleArray(usersWithPhotos),
      correctUserId: factOwner.user.id,
      guessedUserId: null,
      isCorrect: null,
      roundId: Date.now()
    }

    gameState.totalRounds++

    // Preload images for smoother experience
    preloadImages(usersWithPhotos)
  } catch (error) {
    console.error('Error starting round:', error)
    errorMessage.value = t('sevenFacts.roundFailed')
  } finally {
    uiState.isLoadingRound = false
  }
}

async function handleGuess(userId, cardIndex) {
  if (uiState.showingResult) return

  uiState.selectedCardIndex = cardIndex
  gameState.currentRound.guessedUserId = userId
  gameState.currentRound.isCorrect = userId === gameState.currentRound.correctUserId

  // Update streak
  if (gameState.currentRound.isCorrect) {
    gameState.streak++
  } else {
    gameState.streak = 0
  }

  // Show result
  uiState.showingResult = true

  // Track the round (fire and forget)
  trackRound()

  // Auto-advance after delay
  setTimeout(
    () => {
      startNewRound()
    },
    gameState.currentRound.isCorrect ? 1500 : 2500
  )
}

async function checkPlayerCount() {
  try {
    const eligibleCount = await countEligiblePlayers()
    if (eligibleCount >= 4) {
      gameState.phase = 'PLAYING'
      await startNewRound()
    } else {
      errorMessage.value = `${t('sevenFacts.stillNeedPlayers')} ${4 - eligibleCount} ${t('sevenFacts.morePlayers')}`
    }
  } catch (error) {
    console.error('Error checking player count:', error)
    errorMessage.value = t('sevenFacts.checkPlayersFailed')
  }
}

// Helper functions
function getUserPhoto(user) {
  let originalUrl

  if (user.thumbnail_url) {
    originalUrl = user.thumbnail_url
  } else {
    const primaryPhoto = user.photos?.find(p => p.is_primary || p.is_thumbnail)
    if (primaryPhoto) {
      originalUrl = primaryPhoto.photo_url
    } else {
      originalUrl = user.photos?.[0]?.photo_url || '/placeholder.png'
    }
  }

  return getGameImage(originalUrl)
}

function handleImageError(event) {
  if (event.target.src.endsWith('/placeholder.png')) return
  event.target.src = '/placeholder.png'
}

function shuffleArray(array) {
  const newArray = [...array]
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[newArray[i], newArray[j]] = [newArray[j], newArray[i]]
  }
  return newArray
}

function getCardClasses(userId, index) {
  const classes = ['cursor-pointer', 'hover:scale-105', 'transition-transform']

  if (uiState.showingResult) {
    classes.push('cursor-default')
    if (userId === gameState.currentRound.correctUserId) {
      classes.push('ring-4', 'ring-green-500', 'scale-105')
    } else if (index === uiState.selectedCardIndex && !gameState.currentRound.isCorrect) {
      classes.push('shake-animation', 'ring-4', 'ring-red-500')
    }
  }

  return classes
}

function getCorrectUserName() {
  const correctUser = gameState.currentRound.options.find(
    user => user.id === gameState.currentRound.correctUserId
  )
  return correctUser?.name || 'Unknown'
}

function preloadImages(users) {
  users.forEach(user => {
    const img = new Image()
    img.src = getUserPhoto(user)
  })
}

async function trackRound() {
  try {
    await supabase.from('game_rounds').insert({
      player_id: authStore.user.id,
      fact_id: gameState.currentRound.fact.id,
      guess_user_id: gameState.currentRound.guessedUserId,
      is_correct: gameState.currentRound.isCorrect
    })
  } catch (error) {
    console.log('Round tracking failed:', error)
  }
}

async function editFacts() {
  try {
    // Load existing facts into the input fields
    const userFacts = await fetchUserFacts()

    // Clear the input array and populate with existing facts
    factInputs.value = ['', '', '', '', '', '', '']
    userFacts.forEach((fact, index) => {
      if (index < 7) {
        factInputs.value[index] = fact.text
      }
    })

    // Set edit mode and transition to fact entry phase
    uiState.isEditMode = true
    gameState.phase = 'FACT_ENTRY'
  } catch (error) {
    console.error('Error editing facts:', error)
    errorMessage.value = t('sevenFacts.editFactsFailed')
  }
}

async function cancelEdit() {
  if (uiState.isEditMode) {
    uiState.isEditMode = false
    await transitionToAvailablePlayState()
  } else {
    router.back()
  }
}

async function deleteAllFacts() {
  uiState.isDeletingFacts = true

  try {
    const { error } = await supabase.from('facts').delete().eq('user_id', authStore.user.id)

    if (error) throw error

    factInputs.value = ['', '', '', '', '', '', '']
    gameState.userFacts = []
    uiState.isEditMode = false
    uiState.showDeleteConfirmation = false
    gameState.phase = 'FACT_ENTRY'
  } catch (error) {
    console.error('Error deleting facts:', error)
    errorMessage.value = t('sevenFacts.deleteFactsFailed')
  } finally {
    uiState.isDeletingFacts = false
  }
}
</script>

<style scoped>
/* Shake animation for wrong answers */
@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-10px);
  }
  75% {
    transform: translateX(10px);
  }
}

.shake-animation {
  animation: shake 0.5s ease-in-out;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Slide up transition */
.slide-up-enter-active {
  transition: all 0.3s ease-out;
}

.slide-up-enter-from {
  transform: translateY(20px);
  opacity: 0;
}
</style>
